import React, { Fragment } from 'react';
import { differenceInMilliseconds } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { BroadcastInfo, ChannelLogo, CreditItem, CreditList, CreditTitle, Description, DescriptionLayout, HeadlineDescription, HeadlineTitle, ImageContainer, Layout, Lead, MetaInfo, MetaInfoLabels, SubTitle, Title } from './DetailContent.styles';
import { EpisodeAttributes } from './EpisodeAttributes';
import { PlayButtons } from './PlayButtons';
import SvgInfo from '../../../../../play-core/icons/Info';
import { useHydrated } from '../../../hooks/use-hydrated';
import { useViewport } from '../../../providers/ViewportProvider/ViewportContext';
import { dateOrWeekdayFormat } from '../../../utils/date-formatter';
import { formatDuration } from '../../../utils/duration-formatter';
import { DotSeparatedLabels } from '../../DotSeparatedLabels/DotSeparatedLabels';
import { IconWrapper } from '../../IconWrapper/IconWrapper';
import { PlayImage } from '../../Image/PlayImage';
import { PlayContainer, defaultContainerPadding } from '../../PlayContainer/PlayContainer';
import { YouthProtectionInfo } from '../../YouthProtection/YouthProtectionInfo';
import { tvGuideImageRatio } from '../Grid/GridEntry.styles';
import { localTimeSpan } from '../tv-guide-types';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DetailContent = ({
  selectedProgram,
  onClose,
  analyticsPageId
}) => {
  const {
    t
  } = useTranslation();
  const {
    isMediumUp
  } = useViewport();
  const {
    program,
    channel
  } = selectedProgram || {};
  const seasonAndEpisode = getSeasonAndEpisode(program, t);
  const isHydrated = useHydrated();
  return /*#__PURE__*/_jsx(PlayContainer, {
    sidePadding: {
      ...defaultContainerPadding,
      default: 0
    },
    children: /*#__PURE__*/_jsxs(Layout, {
      children: [program.imageUrl && !program.imageIsFallbackUrl && /*#__PURE__*/_jsx(ImageContainer, {
        children: /*#__PURE__*/_jsx(PlayImage, {
          hasBorderRadius: isMediumUp,
          aspectRatio: tvGuideImageRatio,
          src: program.imageUrl,
          width: 480,
          alt: program.title
        })
      }), /*#__PURE__*/_jsxs(DescriptionLayout, {
        children: [/*#__PURE__*/_jsx(Title, {
          children: program.title
        }), program.subtitle && /*#__PURE__*/_jsx(SubTitle, {
          children: program.subtitle
        }), program.blockTitle && /*#__PURE__*/_jsx(SubTitle, {
          children: program.blockTitle
        }), seasonAndEpisode && /*#__PURE__*/_jsx(SubTitle, {
          children: seasonAndEpisode
        }), /*#__PURE__*/_jsxs(MetaInfo, {
          children: [isHydrated && channel && /*#__PURE__*/_jsx(ChannelLogo, {
            alt: channel.title,
            src: channel.imageUrlRaw ?? channel.imageUrl
          }), /*#__PURE__*/_jsx(MetaInfoLabels, {
            children: /*#__PURE__*/_jsx(DotSeparatedLabels, {
              labels: getMetaInfos(program, t)
            })
          })]
        }), program.youthProtectionColor && /*#__PURE__*/_jsx(MetaInfo, {
          children: /*#__PURE__*/_jsx(YouthProtectionInfo, {
            youthProtectionColor: program.youthProtectionColor
          })
        }), program.broadcastInfo && /*#__PURE__*/_jsxs(BroadcastInfo, {
          title: program.broadcastInfo,
          children: [/*#__PURE__*/_jsx(IconWrapper, {
            size: 28,
            icon: /*#__PURE__*/_jsx(SvgInfo, {})
          }), program.broadcastInfo]
        }), /*#__PURE__*/_jsx(PlayButtons, {
          program: program,
          channel: channel,
          analyticsPageId: analyticsPageId,
          onClose: onClose
        }), program.lead && /*#__PURE__*/_jsx(Lead, {
          children: program.lead
        }), program.description && /*#__PURE__*/_jsx(Description, {
          children: program.description
        }), getCredits(program).map(([title, values]) => /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(CreditTitle, {
            children: title
          }), /*#__PURE__*/_jsx(CreditList, {
            children: values.map((value, index) => /*#__PURE__*/_jsx(CreditItem, {
              children: value
            }, index))
          })]
        }, title)), program.headlineList?.map(headLine => /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(HeadlineTitle, {
            children: headLine.title
          }), headLine.description && /*#__PURE__*/_jsx(HeadlineDescription, {
            children: headLine.description
          })]
        }, headLine.title)), /*#__PURE__*/_jsx(EpisodeAttributes, {
          program: program
        })]
      })]
    })
  });
};
const getCredits = program => {
  const result = {};
  program?.creditList?.forEach(item => {
    if (item.role && (item.realName || item.name)) {
      if (!result[item.role]) {
        result[item.role] = [];
      }
      const name = item.realName && item.name ? `${item.realName} (${item.name})` : item.realName ?? item.name;
      result[item.role].push(name);
    }
  });
  return Object.entries(result);
};
const getMetaInfos = (program, t) => [localTimeSpan(program.startTime, program.endTime), dateOrWeekdayFormat(program.startTime, t), formatDuration(differenceInMilliseconds(new Date(program.endTime), new Date(program.startTime)), t), program.productionYear && program.productionCountry ? `${program.productionCountry} ${program.productionYear}` : program.productionCountry || program.productionYear?.toString(), program.genre, program.repetitionDescription];
const getSeasonAndEpisode = (program, t) => {
  const seasonNr = program.seasonNumber ? `${t('global.season')} ${program.seasonNumber}` : undefined;
  const episodeNr = program.episodeNumber ? `${t('global.episode')} ${program.episodeNumber}` : undefined;
  return seasonNr && episodeNr ? `${seasonNr}, ${episodeNr}` : seasonNr ?? episodeNr;
};